import {
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MultiMapContextService } from '@core/services/context/multimap-context.service';
import { RoadAssistanceContextService } from '@core/services/context/road-assistance-context.service';
import { ExternalUrlOpenerHelperService } from '@core/services/core/external-url-opener-helper.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { IonContent, MenuController } from '@ionic/angular';
import { AllFeatureFlags } from '@models/mmobile';
import { MultiMapAccessPoint } from '@models/multimap';
import { ICT_PRICING_ROUTING_PATH } from '@modules/client-area/models/ICTPricing';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { distinctUntilKeyChanged, startWith, tap } from 'rxjs/operators';
import { MenuItem } from 'src/app/core/models/menu';
import { TeCuidamosUserData, User } from 'src/app/core/models/user';
import { Logout } from 'src/app/core/services/core/auth/auth.actions';
import { AuthService } from 'src/app/core/services/core/auth/auth.service';
import { ContextService } from 'src/app/core/services/core/context.service';
import { MenuService } from 'src/app/core/services/core/menu.service';
import { NavigationHelper } from 'src/app/core/services/core/navigation-helper.service';
import { TecuidamosService } from 'src/app/core/services/core/tecuidamos.service';
import { Environment } from '../../../core/services/common';
import { Auth } from '../../../core/services/core/auth/auth.actions';
import { OperationRegisterService } from '../../../core/services/core/operation-register.service';
import { PoliciesHelperService } from '../../../core/services/core/policies-helper.service';
import { FamiliesHelperService } from '../../../core/services/helpers/families-helper.service';
import { ProfilesHelperService } from '../../../core/services/helpers/profile-helper.service';
import { PaymentPlanService } from '../../../core/services/http-services/payment-plan.service';

@Component({
  selector: 'app-menu-component',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit, OnDestroy {
  Math: Math;

  version = '';
  userShorthandAvatar = '';
  authState: Auth;
  teCuidamosState: TeCuidamosUserData;
  userHasCarPolicies = false;
  user: User;
  private availableIctPricing = new BehaviorSubject<boolean>(false);
  private userHasInternalNetworkSubscription: Subscription;
  private showPaymentPlanAccessOnHomePageSubscription: Subscription;
  private medicalCheckUpSubscription: Subscription;

  @ViewChildren('expandableItem') expandableItems: QueryList<MatExpansionPanel>;
  @ViewChild('content', { static: true }) content: IonContent;

  items: MenuItem[] = [];
  loggedItems: MenuItem[] = [
    {
      title: 'Inicio',
      id: 'home',
      route: 'client-area/home'
    },
    {
      title: 'Mis productos',
      id: 'my-products',
      route: 'client-area/home',
      queryParams: { products: true }
    },
    {
      title: 'Club MAPFRE',
      id: 'tecuidamos',
      important: !this.menu.isMultimapEnable,
      route: 'tecuidamos',
      isEnabled: this.menu.availableTeCuidamos$,
      customNavFn: () => {
        this.teCuidamos.navToTeCuidamos(
          'root',
          this.teCuidamosState.memberState,
          'tecuidamos-menu-lateral'
        );
      }
    },
    {
      title: 'Plan de Pagos',
      id: 'payment-plan',
      route: 'payment-plan'
    },
    {
      title: 'Mi familia',
      id: 'my-family',
      route: 'families',
      customNavFn: () => {
        this.onOpenFamilies();
      }
    },
    {
      title: 'MAPFRE teCuidamos',
      id: 'tecuidamos-nexa',
      isEnabled: of(!this.ctx.isApp),
      route: 'tecuidamos-nexa',
      customNavFn: () => {
        this.onOpenTeCuidamosNexa();
      }
    },
    {
      title: 'Reparación y reformas',
      id: 'multimap',
      important: this.menu.isMultimapEnable,
      isEnabled: this.menu.availableMultiMap$,
      route: 'multimap',
      customNavFn: () => {
        this.onOpenMultiMap();
      }
    },
    {
      title: 'Localizador de servicios',
      id: 'services-locator',
      isEnabled: of(this.ctx.isApp),
      childs: [
        {
          title: 'Cuadro médico',
          route: 'locator/medical-center/search',
          state: { healthPolicy: false, reload: true }
        },
        {
          title: 'Oficinas',
          route: 'locator/services-view',
          state: { serviceType: 'OFFICE' }
        },
        {
          title: 'Talleres y Centros de Servicio',
          route: 'locator/garage/search'
        }
      ]
    },
    {
      title: 'Otros productos MAPFRE',
      id: 'other-products',
      important: true,
      isEnabled: this.availableIctPricing.asObservable(),
      route: ICT_PRICING_ROUTING_PATH
    },
    {
      title: 'Contacto',
      id: 'contact',
      route: 'contact',
      isEnabled: this.menu.isContactEnable$
    },
    {
      title: 'Ayuda',
      id: 'help',
      hideOnWeb: true,
      route: 'help'
    },
    {
      title: 'Valora la APP',
      id: 'app-rating',
      hideOnWeb: true,
      route: 'rating',
      isEnabled: this.menu.availableRating$
    }
  ];

  loggedItemsBusiness: MenuItem[] = [
    {
      title: 'Ayuda',
      id: 'help',
      route: 'help',
    },
    {
      title: 'Mis Productos',
      id: 'my-products',
      pwaHideOnDesktop: true,
      route: 'client-area/home'
    },
    {
      title: 'Plan de Pagos',
      id: 'payment-plan',
      pwaHideOnDesktop: true,
      route: 'payment-plan'
    },
    {
      title: 'MAPFRE teCuidamos EMPRESA',
      id: 'tecuidamos',
      pwaHideOnDesktop: true,
      important: true,
      route: 'tecuidamos',
      customNavFn: () => {
        this.urlExternal.openExternalUrl(
          'https://www.mapfretecuidamos.com/empresas/',
          null,
          null,
          { web: { sameTab: false } }
        );
      }
    },
  ];

  loginItems: MenuItem[] = [
    {
      title: 'Teléfonos de información',
      id: 'contact-phones',
      important: true,
      route: 'misc/contacts'
    },
    {
      title: 'Política de privacidad',
      id: 'privacy-policy',
      route: 'operations/rgpdue-policy',
      state: { type: 'generic', onlyShowText: true, menuPolicy: true }
    }
  ];

  loginItemsBusiness: MenuItem[] = [
    {
      title: 'Contacto',
      id: 'contact',
      important: true,
      route: 'login/empresas/contacto'
    },
    {
      title: 'Preguntas frecuentes',
      id: 'faqs',
      route: 'login/empresas/faq',
    }
  ];

  inboxItem: MenuItem = {
    title: 'Buzón',
    id: 'inbox',
    route: 'inbox'
  };

  constructor(
    public menuCtrl: MenuController,
    private nav: NavigationHelper,
    public menu: MenuService,
    public auth: AuthService,
    private or: OperationRegisterService,
    public teCuidamos: TecuidamosService,
    public ctx: ContextService,
    private familiesHelper: FamiliesHelperService,
    private policiesHelper: PoliciesHelperService,
    private paymentPlanService: PaymentPlanService,
    private profileHelper: ProfilesHelperService,
    private urlExternal: ExternalUrlOpenerHelperService,
    private multiMapContext: MultiMapContextService,
    private roadAssistanceCtx: RoadAssistanceContextService,
    private translate: TranslateService,
    private mmobileHelper: MMobileHelperService,
  ) {}

  ngOnInit() {
    this.user = this.auth.getState().user;
    this.version = Environment.config().version;
    this.teCuidamos
      .getState$()
      .subscribe(state => (this.teCuidamosState = state));
    this.auth
      .getState$()
      .pipe(
        startWith({} as Auth),
        tap(state => (this.authState = state)),
        distinctUntilKeyChanged('isLogged')
      )
      .subscribe(state => {
        if (state.isLogged) {
          this.getAvailableIctPricing(state.user);
          this.auth.checkUserIsParticular()
            ? (this.items = this.loggedItems)
            : (this.items = this.loggedItemsBusiness);

          if (this.auth.checkUserIsParticular()) {
            this.items = this.loggedItems;
          }

          if (this.auth.checkUserIsCompany()) {
            this.showPaymentPlanAccessOnHomePageSubscription = this.paymentPlanService.showPaymentPlanAccessOnHomePage$
            .subscribe(showPaymentPlan => {
              showPaymentPlan ?
                this.items = this.loggedItemsBusiness :
                this.items = this.loggedItemsBusiness.filter((el) => el.id !== 'payment-plan');
            });
          }
          this.userShorthandAvatar = this.menu.getUserShorthand(state.user);
          this.checkNumberOfCarPolicies();
        } else {
          this.ctx.accessType$.subscribe({
            next: _ => {
              if (this.ctx.isAccessTypeParticular || this.ctx.isApp) {
                this.items = this.loginItems;
              }
              if (this.ctx.isAccessTypeBusiness && !this.ctx.isApp) {
                this.items = this.loginItemsBusiness;
              }
            }
          });
        }
      });
  }

  ngOnDestroy() {
    if (this.medicalCheckUpSubscription) this.medicalCheckUpSubscription.unsubscribe();
    if (this.showPaymentPlanAccessOnHomePageSubscription) {
      this.showPaymentPlanAccessOnHomePageSubscription.unsubscribe();
    }
    if (this.userHasInternalNetworkSubscription) {
      this.userHasInternalNetworkSubscription.unsubscribe();
    }
  }

  onCloseMenu() {
    this.expandableItems.toArray().forEach(a => a.close());
    this.content.scrollToTop();
  }

  navTo(item: MenuItem) {
    if (!this.auth.getState().isLogged) {
      this.nav.toNew(item?.route, { state: item?.state, queryParams: item?.queryParams });
      this.menuCtrl.close();
      return;
    }

    if (item.customNavFn) {
      item.customNavFn();
    } else {
      this.nav.newRoot(item?.route, { state: item?.state, queryParams: item?.queryParams });
    }
    if (item.route.includes('locator/services-view')) {
      this.menu.servicesViewReload$.next(true);
    }
    this.menuCtrl.close();
  }

  onProfile() {
    this.menuCtrl.close();
    if (this.profileHelper.isTablet()) {
      this.nav.newRoot('profile/home');
    } else {
      this.nav.newRoot('profile');
    }
  }

  onRoadAssistance() {
    this.menuCtrl.close();
    this.roadAssistanceCtx.initializeRoadAssistanceFlow();
  }

  onContacts() {
    this.menuCtrl.close();
    this.nav.newRoot('/misc/contacts');
  }

  onLogout() {
    const isParticular = this.ctx.isAccessTypeParticular;
    this.auth.dispatch$(new Logout({ softLogout: true })).subscribe(
      () => {
        this.nav.goLoginPage(isParticular, { queryParams: { manual: true } });
      }
    );
    this.menuCtrl.close();
  }

  onOpenMultiMap = () => {
    this.multiMapContext.setState({
      policy: null,
      pageCanReturn: false,
      accessPoint: MultiMapAccessPoint.MENU
    });
    this.nav.newRoot('multimap');
  }

  onOpenFamilies() {
    this.familiesHelper.navToFamilies('root', 'menu', false);
  }

  onOpenTeCuidamosNexa() {
    return this.nav.toNew('tecuidamos/tc-transition');
  }

  private checkNumberOfCarPolicies() {
    this.policiesHelper
      .checkUserHasCarPolicies$()
      .subscribe(state => (this.userHasCarPolicies = state));
  }

  private getAvailableIctPricing(user: User) {
    this.userHasInternalNetworkSubscription = this.auth.checkUserHasInternalNetwork$(user).subscribe(
      hasInternalNetwork => this.availableIctPricing.next(hasInternalNetwork)
    );
  }

  enabledRoadAssistance() {
    const enabledRoadAssistanceWEB = this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.ROAD_ASSISTANCE_WEB);
    const enabledRoadAssistanceAPP = (this.ctx.isApp || this.ctx.isMobileOrTablet)
    && this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.ROAD_ASSISTANCE_APP);
    return this.userHasCarPolicies && (enabledRoadAssistanceWEB || enabledRoadAssistanceAPP);
  }
}
